import { useCustomQuery } from "@/shared/hooks/useCustomQuery";
import AppAnalyticsService from "../services/appAnalyticsService";
import { AppAnalyticsSummary, AppGranularity, AppPlatform, ChartResponse, GeographyResponse, ProductsResponse, UsageSummary } from "../types";

export interface UseAppAnalyticsParams {
  start: string;
  end: string;
  platform?: AppPlatform;
  granularity?: AppGranularity;
}

export function useAppAnalyticsSummary(params: UseAppAnalyticsParams) {
  return useCustomQuery<AppAnalyticsService, UseAppAnalyticsParams, AppAnalyticsSummary>({
    serviceInstance: AppAnalyticsService.Instance as unknown as AppAnalyticsService,
    selectAction: (s: AppAnalyticsService) => s.summary,
    parameter: params,
    options: { staleTime: 5 * 60 * 1000, gcTime: 10 * 60 * 1000 },
  });
}

export function useAppAnalyticsTimeseries(params: UseAppAnalyticsParams & { metric: string }) {
  return useCustomQuery<AppAnalyticsService, UseAppAnalyticsParams & { metric: string }, ChartResponse>({
    serviceInstance: AppAnalyticsService.Instance as unknown as AppAnalyticsService,
    selectAction: (s: AppAnalyticsService) => s.timeseries,
    parameter: params,
    options: { staleTime: 60 * 1000 },
  });
}

export function useAppAnalyticsGeography(params: UseAppAnalyticsParams) {
  return useCustomQuery<AppAnalyticsService, UseAppAnalyticsParams, GeographyResponse>({
    serviceInstance: AppAnalyticsService.Instance as unknown as AppAnalyticsService,
    selectAction: (s: AppAnalyticsService) => s.geography,
    parameter: params,
    options: { staleTime: 10 * 60 * 1000 },
  });
}

export function useRevenueSummary(params: UseAppAnalyticsParams) {
  return useCustomQuery<AppAnalyticsService, UseAppAnalyticsParams, {
    total: number; subscriptions: number; iap: number; sales: number; returns: number; gross_total: number;
  }>({
    serviceInstance: AppAnalyticsService.Instance as unknown as AppAnalyticsService,
    selectAction: (s: AppAnalyticsService) => s.revenueSummary,
    parameter: params,
    options: { staleTime: 5 * 60 * 1000 },
  });
}

export function useRevenueTimeseries(params: UseAppAnalyticsParams) {
  return useCustomQuery<AppAnalyticsService, UseAppAnalyticsParams, ChartResponse>({
    serviceInstance: AppAnalyticsService.Instance as unknown as AppAnalyticsService,
    selectAction: (s: AppAnalyticsService) => s.revenueTimeseries,
    parameter: params,
    options: { staleTime: 60 * 1000 },
  });
}

export function useProducts() {
  return useCustomQuery<AppAnalyticsService, Record<string, never>, ProductsResponse>({
    serviceInstance: AppAnalyticsService.Instance as unknown as AppAnalyticsService,
    selectAction: (s: AppAnalyticsService) => s.products,
    parameter: {},
    options: { staleTime: 30 * 60 * 1000 }, // Cache por 30 minutos
  });
}

export function useSubscriptionsSummary(params: UseAppAnalyticsParams) {
  return useCustomQuery<AppAnalyticsService, UseAppAnalyticsParams, {
    active_subscriptions: number;
    active_free_trials: number;
    new_subscriptions: number;
    cancelled_subscriptions: number;
    trial_conversions: number;
    churn: number | string;
    mrr: number | string;
    actual_revenue: number | string;
    gross_revenue: number | string;
  }>({
    serviceInstance: AppAnalyticsService.Instance as unknown as AppAnalyticsService,
    selectAction: (s: AppAnalyticsService) => s.subscriptionsSummary,
    parameter: params,
    options: { staleTime: 5 * 60 * 1000 },
  });
}

export function useUsageSummary(params: UseAppAnalyticsParams) {
  return useCustomQuery<AppAnalyticsService, UseAppAnalyticsParams, UsageSummary>({
    serviceInstance: AppAnalyticsService.Instance as unknown as AppAnalyticsService,
    selectAction: (s: AppAnalyticsService) => s.usageSummary,
    parameter: params,
    options: { staleTime: 5 * 60 * 1000 },
  });
}

export function useUsageTimeseries(params: UseAppAnalyticsParams) {
  return useCustomQuery<AppAnalyticsService, UseAppAnalyticsParams, {
    labels: string[];
    series: {
      sessions: number[];
      dailyActiveDevices: number[];
      crashes: number[];
      payingUsers: number[];
      appStoreViews: number[];
    };
  }>({
    serviceInstance: AppAnalyticsService.Instance as unknown as AppAnalyticsService,
    selectAction: (s: AppAnalyticsService) => s.usageTimeseriesBundle,
    parameter: params,
    options: { staleTime: 60 * 1000 }, // Cache por 1 minuto para dados mais dinâmicos
  });
}

export function useSubscriptionsTimeseries(params: UseAppAnalyticsParams) {
  return useCustomQuery<AppAnalyticsService, UseAppAnalyticsParams, ChartResponse>({
    serviceInstance: AppAnalyticsService.Instance as unknown as AppAnalyticsService,
    selectAction: (s: AppAnalyticsService) => s.subscriptionsTimeseries,
    parameter: params,
    options: { staleTime: 60 * 1000 }, // Cache por 1 minuto para dados mais dinâmicos
  });
}

